import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FC, useRef } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { useTranslation } from 'react-i18next';
import { Button } from '../../button';

type Props = {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedFile: File) => void;
  fileName: string;
};

export const ImageCropperModal: FC<Props> = ({
  open,
  imageSrc,
  onClose,
  onCropComplete,
  fileName
}) => {
  const { t } = useTranslation();
  const cropperRef = useRef<ReactCropperElement>(null);

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    try {
      cropper.getCroppedCanvas().toBlob(
        (blob) => {
          if (!blob) return;

          const croppedFile = new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          onCropComplete(croppedFile);
          onClose();
        },
        'image/jpeg',
        0.95
      );
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('common:CROP_IMAGE')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: '100%',
            maxHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .cropper-container': {
              maxHeight: '60vh !important'
            },
            '& .cropper-canvas': {
              maxHeight: '60vh !important'
            }
          }}
        >
          <Cropper
            className="h-full w-full max-h-[60vh]"
            ref={cropperRef}
            src={imageSrc}
            aspectRatio={1}
            guides={true}
            viewMode={2}
            minCropBoxHeight={100}
            minCropBoxWidth={100}
            background={false}
            responsive={true}
            autoCropArea={0.9}
            checkOrientation={false}
            zoomable={false}
            scalable={false}
            center={true}
            highlight={true}
          />
        </Box>
      </DialogContent>
      <DialogActions className="gap-x-2 pb-4">
        <Button onClick={handleCancel} variant="secondary">
          {t('common:CANCEL')}
        </Button>
        <Button onClick={handleCrop} variant="primary">
          {t('common:SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
